import { Trans, useLingui } from '@lingui/react/macro';
import { Box, Typography } from '@mui/material';
import { Big } from 'big.js';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'ui-kit';
import { TokenInputField } from '@/components/input_field/token_input_field.tsx';
import { useTokenFormatter } from '@/hooks/useTokenFormatter';
import { bigIntToBig, bigToString } from '@/utils/bigUtils';
import { decimalStringToBigInt, type TokenNumberFormatOptions } from '@/utils/tokenNumberUtils.ts';
import { useTokenAmountValidation } from '../../../hooks/useTokenAmountValidation.ts';
import useAppStore from '../../../store/rootStore.ts';
import { useWithdrawProcess } from '../hooks/useWithdrawProcess';

interface WithdrawPanelProps {
  isActive: boolean;
  onClose?: () => void;
  autoFocus?: boolean;
}

export function WithdrawPanel({ isActive, onClose, autoFocus = true }: WithdrawPanelProps) {
  const { t } = useLingui();
  const { token, minWithdraw, maxWithdraw, startWithdrawal, resetProcess, isPending, isSuccess, currentStatus } =
    useWithdrawProcess();
  const isAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const isDelegated = useAppStore((state) => state.isDelegated);

  const formatToken = useTokenFormatter(token);
  const tokenSymbol = token?.symbol || '';
  const [amountInput, setAmountInput] = useState('');

  const displayOptions: Partial<TokenNumberFormatOptions> = useMemo(
    () => ({
      minDecimals: token?.display_precision,
      maxDecimals: token?.decimals,
    }),
    [token]
  );

  const formattedMinWithdraw = useMemo(() => {
    return formatToken(token && minWithdraw != null ? minWithdraw : 0n, displayOptions);
  }, [minWithdraw, token, formatToken, displayOptions]);

  const formattedMaxWithdraw = useMemo(() => {
    if (!token || maxWithdraw?.value == null) return formatToken(0n, displayOptions);
    const dp = Math.min(token.display_precision ?? 2, maxWithdraw.decimals);
    const maxBig = bigIntToBig(maxWithdraw.value, maxWithdraw.decimals);
    const truncatedBigInt = decimalStringToBigInt(bigToString(maxBig, dp, Big.roundDown), token.decimals);
    return formatToken(truncatedBigInt, displayOptions);
  }, [maxWithdraw, token, formatToken, displayOptions]);

  const { error: inputError, isValid } = useTokenAmountValidation({
    inputValue: amountInput,
    token,
    minAmount: minWithdraw,
    maxAmount: maxWithdraw,
    formattedMinAmount: formattedMinWithdraw,
    balanceType: 'collateral',
  });

  const handleClose = useCallback(() => {
    resetProcess();
    onClose?.();
  }, [resetProcess, onClose]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess, handleClose]);

  useEffect(() => {
    if (isActive) {
      if (!isAuthorized) {
        handleClose();
      } else {
        setAmountInput('');
      }
    }
  }, [isActive, isAuthorized, handleClose]);

  const handleSetMax = () => {
    if (maxWithdraw && token) {
      const dp = Math.min(token.display_precision ?? 2, maxWithdraw.decimals);
      const maxBig = bigIntToBig(maxWithdraw.value, maxWithdraw.decimals);
      setAmountInput(bigToString(maxBig, dp, Big.roundDown));
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || !isValid || amountInput.trim() === '') return;

    const amount = decimalStringToBigInt(amountInput, token.decimals);
    startWithdrawal({ amount });
  };

  const getButtonText = () => {
    if (isPending) {
      return t`Processing...`;
    }
    if (!isValid && inputError.type === 'MAX_AMOUNT') {
      return t`Try lower amount`;
    }
    return t({ comment: 'Withdraw panel submit button', message: 'Withdraw' });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        <Trans>
          Withdraw {tokenSymbol} to Monad. Minimum: {formattedMinWithdraw}.
        </Trans>
        {isDelegated ? (
          <Typography component="span" variant="body2" color="warning.main" sx={{ display: 'block', mt: 1 }}>
            <Trans>Funds will be withdrawn from the exchange and sent to the delegated account owner.</Trans>
          </Typography>
        ) : null}
      </Typography>
      <Box sx={{ '& .MuiOutlinedInput-input': { pl: 0 } }}>
        <TokenInputField
          token={token}
          value={amountInput}
          onChange={setAmountInput}
          onMaxClick={handleSetMax}
          disabled={isPending}
          maxAmount={maxWithdraw}
          errorMessage={inputError.message}
          formattedMaxAmount={formattedMaxWithdraw}
          inputId="WithdrawAmount"
          autoFocus={autoFocus}
        />
      </Box>
      {currentStatus === 'confirmed' ? (
        <Button onClick={handleClose} variant="outlined" size="large" fullWidth>
          <Trans>Close</Trans>
        </Button>
      ) : (
        <Button type="submit" variant="contained" size="large" fullWidth disabled={isPending || !isValid}>
          {getButtonText()}
        </Button>
      )}
    </Box>
  );
}
